import { ChangeEvent, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";
import $api from "@/frontend/shared/api/axios";
import { Input } from "@/frontend/shared/ui/Input";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { Typography } from "@/frontend/shared/ui/Typography";
import {
  getRouteDashboard,
  getRouteExpertRootPage,
  getRouteForgotPassword,
  getRouteFounderCompanyProfile
} from "@/frontend/shared/const/router";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import { ERoleAccess } from "@/frontend/app/providers/router/config/routeConfig";
import styles from "./SignInPage.module.scss";
import { loginErrorMessage } from "../const/const";
import { useLocation } from "react-router-dom";
import { LogoSvg } from "@/frontend/shared/assets/LogoSvg";

const CredentialsSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z.string().min(8).max(32)
});

type TCredentials = z.infer<typeof CredentialsSchema>;

export function SignInPage() {
  const { navigate } = useHistoricalNavigate();
  const [credentials, setCredentials] = useState<TCredentials>({ email: "", password: "" });
  const [isErrorShown, setIsErrorShown] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const { search } = useLocation();

  const isValidCredentials = CredentialsSchema.safeParse(credentials).success;
  const mutation = useMutation({
    mutationFn: async (credentials: TCredentials) => {
      const payload = { email: credentials.email, password: credentials.password };
      return await $api.post("/auth/login", payload);
    },
    onSuccess: (response) => {
      snackbarConstructor("Sign in successful", "normal");
      const next = new URLSearchParams(search).get("next");

      if (next) {
        navigate(next);
        return;
      }

      if (response.data.data?.entity === ERoleAccess.FOUNDER) {
        navigate(getRouteFounderCompanyProfile(), { replace: true });
      } else if (response.data.data?.entity === ERoleAccess.EXPERT) {
        navigate(getRouteExpertRootPage(), { replace: true });
      } else {
        navigate(getRouteDashboard(), { replace: true });
      }
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message = err?.response?.data?.message ?? "Failed to sign in";
      snackbarConstructor(message, "error");
      setIsErrorShown(true);
    }
  });

  const gotoLoginPage = () => {
    navigate(getRouteForgotPassword());
  };

  async function getLoginAccess() {
    if (!isValidCredentials) return;
    try {
      await mutation.mutateAsync(credentials);
    } catch {
      return;
    }
  }

  function onChangeEmail(e: ChangeEvent<HTMLInputElement>) {
    setEmailError(undefined);
    setIsErrorShown(false);
    setCredentials((prev) => ({ ...prev, email: e.target.value.trim() }));
  }

  function onChangePassword(e: ChangeEvent<HTMLInputElement>) {
    setIsErrorShown(false);
    setCredentials((prev) => ({ ...prev, password: e.target.value.trim() }));
  }

  return (
    <div className={styles.container}>
      {/* <InnoveraLoginHeader /> */}
      <LogoSvg className={styles.logo} />
      <div className={styles.wrapper}>
        <div className={styles.subContainer}>
          <div className={styles.heading}>
            <Typography variant="heading-5" color="greyscale900" className={styles.title}>
              Log In
            </Typography>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              getLoginAccess();
            }}
            className={styles.form}
          >
            <div className={styles.formFields}>
              <div className={styles.email}>
                <Typography color="greyscale700" variant="label-4">
                  Email
                </Typography>

                <Input
                  // placeholder="Email"
                  value={credentials.email}
                  onChange={onChangeEmail}
                  className={styles.input}
                  inputSize="small"
                  error={emailError}
                  onBlur={() => {
                    setEmailError(
                      CredentialsSchema.safeParse(credentials).error?.issues.find((issue) => issue.path[0] === "email")
                        ?.message
                    );
                  }}
                />
              </div>
              <div className={styles.password}>
                <Typography color="greyscale700" variant="label-4">
                  Password
                </Typography>
                <Input
                  type="password"
                  // placeholder="Password"
                  value={credentials.password}
                  onChange={onChangePassword}
                  className={styles.input}
                  inputSize="small"
                  // isError={isErrorShown}
                />
              </div>
              <div className={styles.forgotPasswordContainer}>
                <NormalButton
                  size="medium"
                  variant="tertiary"
                  className={styles.forgotPasswordButton}
                  type="button"
                  onClick={gotoLoginPage}
                >
                  Forgot Password?
                </NormalButton>
              </div>
            </div>
            <NormalButton
              size="large"
              className={styles.button}
              isDisabled={!isValidCredentials}
              isSubmitting={mutation.isPending}
              type="submit"
            >
              Log In
            </NormalButton>

            <Typography variant="body-3" color="error-red" className={styles.errorMessage}>
              {isErrorShown && loginErrorMessage}
            </Typography>
          </form>
        </div>
      </div>
    </div>
  );
}
